'use client';

import React, { useState } from 'react';
import { X, Package, Phone, Calendar, Scale, FileText, TrendingUp, AlertTriangle, Upload, Eye, Download } from 'lucide-react';

import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';

// ── Variety/Grade mock data for the drawer ─────────────────────────────────
const MOCK_VARIETIES = [
  {
    id: 'v-1',
    varietyName: 'Nasik Big',
    officialGrades: [
      { gradeName: 'Grade A', bags: 30 },
      { gradeName: 'Grade B', bags: 45 },
      { gradeName: 'Grade C', bags: 25 },
    ],
    dealerGrades: [
      { gradeName: 'Grade A', bags: 28 },
      { gradeName: 'Grade B', bags: 47 },
      { gradeName: 'Grade C', bags: 25 },
    ],
  },
  {
    id: 'v-2',
    varietyName: 'Small Onion',
    officialGrades: [
      { gradeName: 'Grade A', bags: 30 },
      { gradeName: 'Grade B', bags: 45 },
      { gradeName: 'Grade C', bags: 25 },
    ],
    dealerGrades: [
      { gradeName: 'Grade A', bags: 27 },
      { gradeName: 'Grade B', bags: 48 },
      { gradeName: 'Grade C', bags: 25 },
    ],
  },
];

interface LotDetailDrawerProps {
  lot: {
    id: string;
    lotNumber: string;
    product: string;
    distributor: string;
    distributorMobile: string;
    distributorCity: string;
    arrivalDate: string;
    totalBags: number;
    totalWeight: number;
    soldBags: number;
    soldWeight: number;
    status: 'active' | 'arrived' | 'settled' | 'pending' | 'closed' | 'graded' | 'partial';
    officialGrades: { A: number; B: number; C: number };
    dealerGrades: { A: number; B: number; C: number };
    docsCount: number;
    salesValue: number;
    expenseTotal: number;
    paymentsReceived: number;
    remarks: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

const TABS = ['Overview', 'Grading', 'Sales', 'Payments', 'Documents', 'Timeline'];

const MOCK_TIMELINE = [
  { id: 'tl-1', event: 'Lot arrived at Koyambedu', time: '16 Jun 07:30 AM', icon: '🚛' },
  { id: 'tl-2', event: 'Weighbridge slip uploaded', time: '16 Jun 08:15 AM', icon: '📄' },
  { id: 'tl-3', event: 'Official grade entry completed', time: '16 Jun 09:00 AM', icon: '📋' },
  { id: 'tl-4', event: 'Dealer grade verification done', time: '16 Jun 10:30 AM', icon: '✅' },
  { id: 'tl-5', event: 'First sale — A Grade, 30 bags to Senthil Traders', time: '16 Jun 11:00 AM', icon: '🛒' },
  { id: 'tl-6', event: 'Payment ₹22,500 received — UPI', time: '16 Jun 02:30 PM', icon: '💰' },
  { id: 'tl-7', event: 'Sale — B Grade, 25 bags to Murugan Wholesale', time: '17 Jun 09:15 AM', icon: '🛒' },
];

export default function LotDetailDrawer({ lot, isOpen, onClose }: LotDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState('Overview');
  const [gradingView, setGradingView] = useState<'official' | 'dealer'>('official');

  if (!isOpen) return null;

  const soldPct = Math.round((lot.soldBags / lot.totalBags) * 100);
  const netSettlement = lot.salesValue - lot.expenseTotal - lot.paymentsReceived;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[480px] lg:w-[520px] bg-card border-l border-border z-50 flex flex-col animate-slide-up overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Package size={18} className="text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="lot-number text-lg font-bold text-primary">#{lot.lotNumber}</span>
                <StatusBadge status={lot.status} />
              </div>
              <p className="text-xs text-muted-foreground">{lot.product} — {lot.distributor}</p>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost p-2">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto scrollbar-none border-b border-border px-2">
          {TABS.map((tab) => (
            <button
              key={`drawer-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`flex-shrink-0 px-4 py-3 text-xs font-semibold border-b-2 transition-all ${
                activeTab === tab
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {activeTab === 'Overview' && (
            <>
              {/* Key Info */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Arrival Date', value: lot.arrivalDate, icon: Calendar },
                  { label: 'Total Bags', value: `${lot.totalBags} bags`, icon: Package },
                  { label: 'Total Weight', value: `${lot.totalWeight.toLocaleString('en-IN')} kg`, icon: Scale },
                  { label: 'Distributor Mobile', value: lot.distributorMobile, icon: Phone },
                ].map((info) => {
                  const Icon = info.icon;
                  return (
                    <div key={`info-${info.label}`} className="bg-muted/30 rounded-xl p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Icon size={12} className="text-muted-foreground" />
                        <p className="text-2xs text-muted-foreground">{info.label}</p>
                      </div>
                      <p className="text-sm font-semibold text-foreground">{info.value}</p>
                    </div>
                  );
                })}
              </div>

              {/* Sales Progress */}
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-foreground">Sales Progress</p>
                  <span className="text-sm font-bold text-primary">{soldPct}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full ${soldPct === 100 ? 'bg-accent' : 'bg-primary'}`}
                    style={{ width: `${soldPct}%` }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-muted-foreground">Sold</p>
                    <p className="font-semibold">{lot.soldBags} bags / {lot.soldWeight.toLocaleString('en-IN')} kg</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Remaining</p>
                    <p className="font-semibold">{lot.totalBags - lot.soldBags} bags / {(lot.totalWeight - lot.soldWeight).toLocaleString('en-IN')} kg</p>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-muted/30 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-foreground mb-3">Financial Summary</p>
                {[
                  { label: 'Total Sales Value', value: `₹${lot.salesValue.toLocaleString('en-IN')}`, color: 'text-accent' },
                  { label: 'Total Expenses', value: `-₹${lot.expenseTotal.toLocaleString('en-IN')}`, color: 'text-danger' },
                  { label: 'Payments Received', value: `₹${lot.paymentsReceived.toLocaleString('en-IN')}`, color: 'text-info' },
                  { label: 'Net Pending', value: `₹${Math.abs(netSettlement).toLocaleString('en-IN')}`, color: netSettlement > 0 ? 'text-warning' : 'text-accent' },
                ].map((fin) => (
                  <div key={`fin-${fin.label}`} className="flex items-center justify-between text-sm border-b border-border/50 pb-2 last:border-0 last:pb-0">
                    <span className="text-muted-foreground">{fin.label}</span>
                    <span className={`font-bold amount-display ${fin.color}`}>{fin.value}</span>
                  </div>
                ))}
              </div>

              {/* Remarks */}
              {lot.remarks && (
                <div className="bg-muted/20 rounded-xl p-3 border border-border">
                  <p className="text-xs text-muted-foreground mb-1">Remarks</p>
                  <p className="text-sm text-foreground">{lot.remarks}</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'Grading' && (
            <>
              <div className="space-y-4">
                {/* View toggle */}
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">Variety &amp; Grade Breakdown</p>
                  <div className="flex items-center bg-muted rounded-lg p-0.5 gap-0.5">
                    <button
                      type="button"
                      onClick={() => setGradingView('official')}
                      className={`text-2xs font-semibold px-2.5 py-1 rounded-md transition-all ${
                        gradingView === 'official' ?'bg-card text-primary shadow-sm' :'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Official
                    </button>
                    <button
                      type="button"
                      onClick={() => setGradingView('dealer')}
                      className={`text-2xs font-semibold px-2.5 py-1 rounded-md transition-all ${
                        gradingView === 'dealer' ?'bg-card text-accent shadow-sm' :'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Dealer
                    </button>
                  </div>
                </div>

                {/* View label */}
                <div className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg ${
                  gradingView === 'official' ?'bg-primary/5 text-primary border border-primary/20' :'bg-accent/5 text-accent border border-accent/20'
                }`}>
                  {gradingView === 'official' ? '📋 Official Distributor Grading' : '✅ Dealer Verification Grading'}
                </div>

                {/* Variety cards */}
                {MOCK_VARIETIES.map((variety) => {
                  const grades = gradingView === 'official' ? variety.officialGrades : variety.dealerGrades;
                  const varietyTotal = grades.reduce((s, g) => s + g.bags, 0);
                  return (
                    <div key={variety.id} className="border border-border rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between bg-muted/30 px-3 py-2.5">
                        <p className="text-sm font-bold text-foreground">{variety.varietyName}</p>
                        <span className="text-xs font-semibold text-muted-foreground">{varietyTotal} bags</span>
                      </div>
                      <div className="p-3 space-y-2">
                        {grades.map((grade, gIdx) => {
                          const officialBags = variety.officialGrades[gIdx]?.bags ?? 0;
                          const dealerBags = variety.dealerGrades[gIdx]?.bags ?? 0;
                          const variance = dealerBags - officialBags;
                          return (
                            <div key={`${variety.id}-grade-${gIdx}`} className="flex items-center gap-3">
                              <span className="text-xs font-medium text-muted-foreground w-20 flex-shrink-0">{grade.gradeName}</span>
                              <div className="flex-1 bg-muted rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${gradingView === 'official' ? 'bg-primary' : 'bg-accent'}`}
                                  style={{ width: `${lot.totalBags > 0 ? (grade.bags / lot.totalBags) * 100 : 0}%` }}
                                />
                              </div>
                              <span className="text-xs font-semibold w-16 text-right font-mono">{grade.bags} bags</span>
                              {gradingView === 'dealer' && variance !== 0 && (
                                <span className={`text-2xs font-bold w-10 text-right ${variance > 0 ? 'text-accent' : 'text-danger'}`}>
                                  {variance > 0 ? '+' : ''}{variance}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Overall grade summary (legacy A/B/C) */}
                <div className="bg-warning/5 border border-warning/20 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} className="text-warning" />
                    <p className="text-xs font-semibold text-warning">Grade Variance Summary (Official vs Dealer)</p>
                  </div>
                  {(['A', 'B', 'C'] as const).map((grade) => {
                    const diff = lot.dealerGrades[grade] - lot.officialGrades[grade];
                    const pct = lot.officialGrades[grade] > 0 ? ((diff / lot.officialGrades[grade]) * 100).toFixed(1) : '0';
                    return (
                      <div key={`variance-${grade}`} className="flex items-center justify-between text-xs py-1">
                        <span className="text-muted-foreground">{grade} Grade</span>
                        <span className={`font-semibold ${Math.abs(diff) > 3 ? 'text-warning' : 'text-muted-foreground'}`}>
                          {diff > 0 ? '+' : ''}{diff} bags ({pct}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {activeTab === 'Timeline' && (
            <div className="space-y-3">
              {MOCK_TIMELINE.map((event, idx) => (
                <div key={event.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-xl bg-muted flex items-center justify-center text-sm">
                      {event.icon}
                    </div>
                    {idx < MOCK_TIMELINE.length - 1 && (
                      <div className="w-px flex-1 bg-border mt-1" />
                    )}
                  </div>
                  <div className="flex-1 pb-3">
                    <p className="text-sm font-medium text-foreground">{event.event}</p>
                    <p className="text-2xs text-muted-foreground mt-0.5">{event.time}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'Documents' && (
            <div className="space-y-3">
              {[
                { id: 'doc-1', name: 'Weighbridge Slip', type: 'PDF', size: '245 KB', date: '16 Jun' },
                { id: 'doc-2', name: 'Lorry Receipt', type: 'Image', size: '1.2 MB', date: '16 Jun' },
                { id: 'doc-3', name: 'Arrival Photo', type: 'Image', size: '2.8 MB', date: '16 Jun' },
              ].map((doc) => (
                <div key={doc.id} className="flex items-center gap-3 bg-muted/30 rounded-xl p-3">
                  <div className="w-9 h-9 rounded-lg bg-info/10 flex items-center justify-center">
                    <FileText size={16} className="text-info" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{doc.name}</p>
                    <p className="text-2xs text-muted-foreground">{doc.type} · {doc.size} · {doc.date}</p>
                  </div>
                  <div className="flex gap-1">
                    <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground">
                      <Eye size={14} />
                    </button>
                    <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground">
                      <Download size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <button className="w-full border-2 border-dashed border-border rounded-xl p-4 flex items-center justify-center gap-2 text-muted-foreground hover:border-primary/30 hover:text-primary transition-colors">
                <Upload size={16} />
                <span className="text-sm font-medium">Upload Document</span>
              </button>
            </div>
          )}

          {(activeTab === 'Sales' || activeTab === 'Payments') && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                View full {activeTab.toLowerCase()} in the{' '}
                <span className="text-primary font-medium">{activeTab}</span> module
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-border p-4 flex gap-3">
          <button className="btn-secondary flex-1 text-sm">
            <FileText size={14} />
            Report
          </button>
          <button className="btn-primary flex-1 text-sm">
            <TrendingUp size={14} />
            Settle Lot
          </button>
        </div>
      </div>
    </>
  );
}